import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import Navigation from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import {
  ClipboardCheck,
  BookOpen,
  CheckCircle,
  XCircle,
  Clock,
  Filter,
  Search,
  ChevronRight,
  AlertTriangle,
  Eye,
  Edit,
  MessageSquare,
  BarChart3,
  FileText,
  Flame,
  Calendar,
} from "lucide-react";

const SMEDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState({
    total_questions: 0,
    pending_review: 0,
    approved: 0,
    rejected: 0,
  });
  const [pendingQuestions, setPendingQuestions] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [filterSubject, setFilterSubject] = useState("all");

  useEffect(() => {
    fetchSMEData();
  }, []);

  const fetchSMEData = async () => {
    try {
      // Fetch SME dashboard data
      const [dashboardRes, questionsRes] = await Promise.all([
        axios.get(`${API}/sme/dashboard`),
        axios.get(`${API}/questions?limit=50`),
      ]);

      setStats({
        total_questions: dashboardRes.data.total_questions || 0,
        pending_review: dashboardRes.data.pending_review || 0,
        approved: dashboardRes.data.total_questions - (dashboardRes.data.pending_review || 0),
        rejected: 0,
      });
      setPendingQuestions(dashboardRes.data.recent_pending || []);
      
      // Mock recent activity for now
      setRecentActivity([
        { action: "Approved", question: "Describe brachial plexus...", time: "2 hours ago" },
        { action: "Commented", question: "Explain cardiac cycle...", time: "5 hours ago" },
        { action: "Rejected", question: "Name the bones of skull...", time: "1 day ago" },
      ]);
    } catch (error) {
      console.error("Error fetching SME data:", error);
      if (error.response?.status === 403) {
        toast.error("SME access required. Contact admin.");
        navigate("/dashboard");
      } else {
        toast.error("Failed to load SME dashboard");
      }
    } finally {
      setLoading(false);
    }
  };

  const openReviewModal = (question) => {
    setSelectedQuestion(question);
    setReviewModalOpen(true);
  };

  const handleApprove = async () => {
    toast.success("Question approved successfully!");
    setReviewModalOpen(false);
    fetchSMEData();
  };

  const handleReject = async () => {
    toast.error("Question rejected and sent back for revision.");
    setReviewModalOpen(false);
    fetchSMEData();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-[#2E3A59] font-medium">Loading SME Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navigation role="sme" />
      
      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
          
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl lg:text-3xl font-heading font-bold text-[#2E3A59]">
              SME Review Dashboard
            </h1>
            <p className="text-slate-500 mt-1">
              Review and approve questions for the question bank
            </p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-xl border border-slate-100 p-4 lg:p-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 lg:w-12 lg:h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <BookOpen className="w-5 h-5 lg:w-6 lg:h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl lg:text-3xl font-bold text-[#2E3A59]">{stats.total_questions}</p>
                  <p className="text-xs lg:text-sm text-slate-500">Total Questions</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-100 p-4 lg:p-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 lg:w-12 lg:h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                  <Clock className="w-5 h-5 lg:w-6 lg:h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl lg:text-3xl font-bold text-amber-600">{stats.pending_review}</p>
                  <p className="text-xs lg:text-sm text-slate-500">Pending Review</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-100 p-4 lg:p-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 lg:w-12 lg:h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 lg:w-6 lg:h-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl lg:text-3xl font-bold text-emerald-600">{stats.approved}</p>
                  <p className="text-xs lg:text-sm text-slate-500">Approved</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-100 p-4 lg:p-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 lg:w-12 lg:h-12 bg-red-100 rounded-xl flex items-center justify-center">
                  <XCircle className="w-5 h-5 lg:w-6 lg:h-6 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl lg:text-3xl font-bold text-red-600">{stats.rejected}</p>
                  <p className="text-xs lg:text-sm text-slate-500">Rejected</p>
                </div>
              </div>
            </div>
          </div>

          {/* Main Grid */}
          <div className="grid lg:grid-cols-12 gap-6">
            {/* Pending Reviews */}
            <div className="lg:col-span-8">
              <div className="bg-white rounded-xl border border-slate-100 p-4 lg:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                  <h2 className="font-semibold text-[#2E3A59] flex items-center gap-2">
                    <ClipboardCheck className="w-5 h-5 text-purple-600" />
                    Questions Pending Review
                  </h2>
                  
                  <div className="flex items-center gap-2">
                    <select
                      value={filterSubject}
                      onChange={(e) => setFilterSubject(e.target.value)}
                      className="text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="all">All Subjects</option>
                      <option value="Anatomy">Anatomy</option>
                      <option value="Physiology">Physiology</option>
                      <option value="Biochemistry">Biochemistry</option>
                    </select>
                  </div>
                </div>

                {pendingQuestions.length > 0 ? (
                  <div className="space-y-3">
                    {pendingQuestions.map((question, i) => (
                      <div
                        key={question.question_id || i}
                        className="p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors cursor-pointer"
                        onClick={() => openReviewModal(question)}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded-full">
                                {question.meta?.subject || "Subject"}
                              </span>
                              <span className="text-xs px-2 py-1 bg-slate-200 text-slate-600 rounded-full">
                                Sem {question.meta?.semester || 1}
                              </span>
                              {question.frequency?.label === "HOT" && (
                                <span className="text-xs px-2 py-1 bg-orange-100 text-orange-700 rounded-full flex items-center gap-1">
                                  <Flame className="w-3 h-3" />
                                  HOT
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-[#2E3A59] font-medium line-clamp-2">
                              {question.question_text}
                            </p>
                            <p className="text-xs text-slate-400 mt-2">
                              {question.marks_total || question.marks} marks · {question.question_type}
                            </p>
                          </div>
                          <Button size="sm" variant="outline" className="shrink-0">
                            <Eye className="w-4 h-4 mr-1" />
                            Review
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <CheckCircle className="w-16 h-16 text-emerald-200 mx-auto mb-4" />
                    <h3 className="font-semibold text-[#2E3A59] mb-2">All caught up!</h3>
                    <p className="text-sm text-slate-400">No questions pending review.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-4 space-y-6">
              {/* Quick Actions */}
              <div className="bg-white rounded-xl border border-slate-100 p-4 lg:p-6">
                <h3 className="font-semibold text-[#2E3A59] mb-4">Quick Actions</h3>
                <div className="space-y-2">
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => navigate("/sme/questions")}
                  >
                    <BookOpen className="w-4 h-4 mr-2" />
                    Browse Question Bank
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => navigate("/sme/review")}
                  >
                    <ClipboardCheck className="w-4 h-4 mr-2" />
                    Review Queue
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Upload Questions
                  </Button>
                </div>
              </div>

              {/* Recent Activity */}
              <div className="bg-white rounded-xl border border-slate-100 p-4 lg:p-6">
                <h3 className="font-semibold text-[#2E3A59] mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-purple-600" />
                  Recent Activity
                </h3>
                <div className="space-y-3">
                  {recentActivity.map((activity, i) => (
                    <div key={i} className="flex items-start gap-3 pb-3 border-b border-slate-100 last:border-0">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        activity.action === "Approved" ? "bg-emerald-100" :
                        activity.action === "Rejected" ? "bg-red-100" : "bg-blue-100"
                      }`}>
                        {activity.action === "Approved" ? (
                          <CheckCircle className="w-4 h-4 text-emerald-600" />
                        ) : activity.action === "Rejected" ? (
                          <XCircle className="w-4 h-4 text-red-600" />
                        ) : (
                          <MessageSquare className="w-4 h-4 text-blue-600" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-[#2E3A59]">
                          <span className="font-medium">{activity.action}</span>: {activity.question.substring(0, 30)}...
                        </p>
                        <p className="text-xs text-slate-400">{activity.time}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Stats by Subject */}
              <div className="bg-white rounded-xl border border-slate-100 p-4 lg:p-6">
                <h3 className="font-semibold text-[#2E3A59] mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-purple-600" />
                  Questions by Subject
                </h3>
                <div className="space-y-3">
                  {[
                    { subject: "Anatomy", count: 289, color: "bg-blue-500" },
                    { subject: "Physiology", count: 318, color: "bg-rose-500" },
                    { subject: "Biochemistry", count: 271, color: "bg-purple-500" },
                  ].map((item, i) => (
                    <div key={i}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-slate-600">{item.subject}</span>
                        <span className="text-sm font-semibold text-[#2E3A59]">{item.count}</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${item.color} rounded-full`}
                          style={{ width: `${(item.count / 320) * 100}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Review Modal */}
      <Dialog open={reviewModalOpen} onOpenChange={setReviewModalOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Review Question</DialogTitle>
          </DialogHeader>
          
          {selectedQuestion && (
            <div className="py-4 space-y-4">
              {/* Question Info */}
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded-full">
                  {selectedQuestion.meta?.subject || "Subject"}
                </span>
                <span className="text-xs px-2 py-1 bg-slate-200 text-slate-600 rounded-full">
                  Sem {selectedQuestion.meta?.semester || 1}
                </span>
                <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
                  {selectedQuestion.question_type}
                </span>
                <span className="text-xs px-2 py-1 bg-amber-100 text-amber-700 rounded-full">
                  {selectedQuestion.marks_total || selectedQuestion.marks} marks
                </span>
              </div>

              {/* Question Text */}
              <div className="p-4 bg-slate-50 rounded-xl">
                <p className="text-xs text-slate-500 mb-2">Question</p>
                <p className="text-[#2E3A59] font-medium question-text">
                  {selectedQuestion.question_text}
                </p>
              </div>

              {/* Rubric */}
              <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
                <p className="text-xs text-blue-600 font-semibold mb-2">Rubric / Marking Scheme</p>
                {selectedQuestion.rubric?.must_include_points?.length > 0 ? (
                  <ul className="text-sm text-blue-800 space-y-1">
                    {selectedQuestion.rubric.must_include_points.map((point, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="text-blue-400">•</span>
                        {point}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-blue-700">No rubric defined yet.</p>
                )}
              </div>

              {/* Frequency */}
              {selectedQuestion.source?.times_asked > 0 && (
                <div className="p-4 bg-orange-50 rounded-xl border border-orange-100">
                  <p className="text-xs text-orange-600 font-semibold mb-2">Exam History</p>
                  <p className="text-sm text-orange-800">
                    Asked {selectedQuestion.source.times_asked} times · Last in {selectedQuestion.source.years?.[0] || "N/A"}
                  </p>
                </div>
              )}

              {/* Review Notes */}
              <div>
                <label className="text-xs text-slate-500 mb-2 block">Review Notes (optional)</label>
                <textarea
                  className="w-full p-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  rows={3}
                  placeholder="Add notes or suggestions..."
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t">
                <Button
                  onClick={handleReject}
                  variant="outline"
                  className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Reject
                </Button>
                <Button
                  onClick={handleApprove}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approve
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SMEDashboard;
